import React from "react";

import {
  getStorageDegradedReason,
  getStorageMode,
  isStorageDegraded,
  probeStorageCapability,
  subscribeStorageMode,
  type StorageMode,
} from "@/lib/storageCapability";

export type StorageCapabilityState = {
  mode: StorageMode;
  degraded: boolean;
  reason: string | null;
  ready: boolean;
};

/**
 * React subscription to storage mode (IDB probe + runtime degrade marks).
 * Safe to use anywhere under the app tree; does not touch Dexie itself.
 */
export function useStorageCapability(): StorageCapabilityState {
  const [mode, setMode] = React.useState<StorageMode>(() => getStorageMode());
  const [reason, setReason] = React.useState<string | null>(() => getStorageDegradedReason());
  const [ready, setReady] = React.useState(() => isStorageDegraded());

  React.useEffect(() => {
    let cancelled = false;
    void probeStorageCapability().then((m) => {
      if (cancelled) return;
      setMode(m);
      setReason(getStorageDegradedReason());
      setReady(true);
    });
    const unsub = subscribeStorageMode((m, r) => {
      setMode(m);
      setReason(r);
      setReady(true);
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  return { mode, degraded: mode === "degraded", reason, ready };
}
